<template>
  <div>
    <v-autocomplete
      clearable
      outlined
      dense
      item-value="id"
      hide-details
      prepend-inner-icon="mdi-calendar-clock-outline"
      :label="$t('common.date_options.creation_date')"
      :items="creationDateOptions"
      :value="selectedFilters.createdAtOption"
      @change="handleDateChange($event)"
    />
    <v-menu
      v-if="showCustomCreationDate"
      offset-y
      class="mt-4"
      :close-on-content-click="false"
    >
      <template v-slot:activator="{ on }">
        <v-text-field
          :value="dateRangeText"
          v-on="on"
          dense
          readonly
          hide-details
          outlined
          event-color="primary"
          prepend-inner-icon="mdi-calendar-outline"
          class="font-size-14 my-4"
          :label="$t('common.date_options.date_range')"
        />
      </template>

      <v-date-picker
        v-model="customDateValues"
        style="width: 100%;"
        no-title
        range
        reactive
        :value="dateValues"
        :allowed-dates="isCreationDateAllowed"
        @change="onDateRangeChange($event)"
      />
    </v-menu>
  </div>
</template>

<script>
import * as moment from 'moment'
import _ from 'lodash'
import { utils } from 'frontend-js-shared'

import filtersConstants from './Filters/constants'
import { getDateRangeText } from './dateRangeText'

const { dates } = utils
const DATE_FILTER_OPTIONS = filtersConstants.DATE_FILTER_OPTIONS
const CREATION_DATE_OPTIONS = [
  {
    id: DATE_FILTER_OPTIONS.all,
    textKey: 'common.date_periods.all',
  },
  ...filtersConstants.COMMON_DATE_OPTIONS,
]

export default {
  name: 'CreatedDateSelector',

  props: {
    selectedFilters: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      today: moment().format('YYYY-MM-DD'),
      creationDateOptions: CREATION_DATE_OPTIONS.map(option => ({
        id: option.id,
        text: this.$t(option.textKey ?? option.text),
      })),
      customDateValues: [],
    }
  },

  mounted() {
    if (!this.selectedFilters?.createdAtOption) {
      this.handleDateChange(DATE_FILTER_OPTIONS.lastMonth)
    }
  },

  computed: {
    showCustomCreationDate() {
      return (
        this.selectedFilters?.createdAtOption === DATE_FILTER_OPTIONS.custom ??
        false
      )
    },

    dateRangeText() {
      return getDateRangeText(Object.values(this.selectedFilters?.createdAt ?? {}))
    },

    dateValues() {
      return _.isEmpty(this.selectedFilters?.createdAt)
        ? []
        : Object.values(this.selectedFilters?.createdAt)
    },
  },

  methods: {
    isCreationDateAllowed(date) {
      return date <= this.today
    },

    handleDateChange(dateRange) {
      switch (dateRange) {
        case DATE_FILTER_OPTIONS.all:
          this.formatDateForFilter(DATE_FILTER_OPTIONS.all, [], 'createdAt')
          break
        case DATE_FILTER_OPTIONS.last7Days:
          this.formatDateForFilter(
            DATE_FILTER_OPTIONS.last7Days,
            Object.values(
              dates.getPredefinedDateRange(DATE_FILTER_OPTIONS.last7Days)
            ),
            'createdAt'
          )
          break
        case DATE_FILTER_OPTIONS.lastMonth:
          this.formatDateForFilter(
            DATE_FILTER_OPTIONS.lastMonth,
            Object.values(
              dates.getPredefinedDateRange(DATE_FILTER_OPTIONS.lastMonth)
            ),
            'createdAt'
          )
          break
        case DATE_FILTER_OPTIONS.last90Days:
          this.formatDateForFilter(
            DATE_FILTER_OPTIONS.last90Days,
            Object.values(
              dates.getPredefinedDateRange(DATE_FILTER_OPTIONS.last90Days)
            ),
            'createdAt'
          )
          break
        case DATE_FILTER_OPTIONS.thisYear:
          this.formatDateForFilter(
            DATE_FILTER_OPTIONS.thisYear,
            Object.values(
              dates.getPredefinedDateRange(DATE_FILTER_OPTIONS.thisYear)
            ),
            'createdAt'
          )
          break
        case DATE_FILTER_OPTIONS.custom:
          this.formatDateForFilter(
            DATE_FILTER_OPTIONS.custom,
            [],
            'createdAtOption'
          )
          break
        default:
          this.formatDateForFilter('', [], 'createdAtOption')
      }
    },

    onDateRangeChange(range = []) {
      const [startDate, endDate] = range
      const startDateTime = moment(startDate).format()
      const endDateTime = moment(endDate)
        .endOf('day')
        .format()

      if (startDateTime > endDateTime)
        this.$emit(
          'change',
          { from: endDateTime, to: startDateTime },
          'createdAt'
        )
      else
        this.$emit(
          'change',
          { from: startDateTime, to: endDateTime },
          'createdAt'
        )
    },

    formatDateForFilter(option, value, key) {
      if (_.isEmpty(value)) {
        this.$emit('change', [], 'createdAt')
        this.$emit('change', option, 'createdAtOption')
        return
      }
      this.$emit(
        'change',
        {
          from: moment(value[0]).format(),
          to: moment(value[1])
            .endOf('day')
            .format(),
        },
        key
      )
      this.$emit('change', option, 'createdAtOption')
    },
  },
}
</script>
